import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyStateCell } from '@components/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconPersonOff } from '@components/icons/person-off';
import { IconRemove } from '@components/icons/remove';
import { Menu } from '@components/menu';
import { PageContainers } from '@components/page-containers';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useGetBlackListedEmails, useRemoveBlockedEmail } from 'api/blacklisted/hooks';
import { BlacklistedEmail } from 'api/blacklisted/requests';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { SettingsSection } from 'components/settings-section';
import { useModal } from 'features/modals';

export const Blacklist = () => {
  const { t } = useScopedTranslate(locale);
  const { showModal } = useModal();
  const emailsQuery = useGetBlackListedEmails();
  const removeBlockedMutation = useRemoveBlockedEmail();
  const blockedEmails = emailsQuery.data;

  function openAddEmailModal(blockedEmail?: BlacklistedEmail) {
    showModal('SETTINGS_ADD_BLACKLIST_EMAIL', {
      blockedEmail,
    });
  }
  if (!blockedEmails) {
    return <Loading />;
  }
  return (
    <>
      <Header breadcrumbs={<Breadcrumb items={[{ label: t('spamProtection') }]} />} />
      <PageContent>
        <PageContainers.MiddleColumnPage>
          <SettingsSection title={t('blockEmails')} description={t('blockEmailsDescription')}>
            {blockedEmails.length === 0 ? (
              <EmptyStateCell icon={<IconPersonOff />} text={t('noEmailsBlocked')} />
            ) : (
              blockedEmails.map((blocked, idx) => (
                <Card variant="default" spaceX={6} spaceY={4} interactive key={idx}>
                  <Stack orientation="horizontal" space={4} justify="space-between">
                    <div>
                      <Text font="semibold14" text={blocked.email} />
                      <Text font="regular14" color="purple80" text={blocked.description} />
                    </div>
                    <Menu
                      buttonProps={{
                        variant: 'ghost',
                        iconLeft: <IconMoreVertical />,
                      }}
                    >
                      <Menu.Item
                        icon={<IconEdit />}
                        variant="base"
                        label={t('edit')}
                        onSelect={() => openAddEmailModal(blocked)}
                      />
                      <Menu.Item
                        icon={<IconRemove />}
                        variant="danger"
                        label={t('unblock')}
                        onSelect={() => removeBlockedMutation.mutate(blocked.id)}
                      />
                    </Menu>
                  </Stack>
                </Card>
              ))
            )}
            <Stack space={4} align="flex-end">
              <Button
                variant="secondary"
                iconLeft={<IconAddPlus />}
                text={t('addEmail')}
                onClick={() => openAddEmailModal()}
              />
            </Stack>
          </SettingsSection>
        </PageContainers.MiddleColumnPage>
      </PageContent>
    </>
  );
};

const locale = {
  en: {
    spamProtection: 'Blocklist',
    blockEmails: 'Block emails',
    blockEmailsDescription:
      'To block unwanted applications, manually add emails that are spamming or submitting an excessive number of applications. The application will not appear in the system. Blocked applicants will not be notified or made aware when submitting',
    addEmail: 'Add email',
    noEmailsBlocked: 'No emails blocked',
    edit: 'Edit',
    unblock: 'Unblock email',
  },
  is: {
    blockEmails: 'Loka fyrir netföng',
    blockEmailsDescription:
      'Loka fyrir umsóknir frá netföngum sem eru að sækja óvenjulega oft um störf. Umsókn mun ekki birtast í kerfinu. Umsækjandinn mun ekki verða var við þessa aðgerð',
    addEmail: 'Bæta við netfangi',
    spamProtection: 'Bannlisti',
    noEmailsBlocked: 'Engin netföng skráð',
    edit: 'Breyta',
    unblock: 'Opna fyrir netfang',
  },
};
